import {
	Box,
	Grid,
	IconButton,
	Stack,
	TextField,
	Typography,
	styled,
} from '@mui/material';

import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import { Controller, useForm } from 'react-hook-form';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { PAGINATION_CONSTANTS } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { DATA_ACCESS_PERMISSIONS_ACTIONS } from 'common/constants/PermissionsConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setGroupPermissionDrawer } from 'common/store/reducer/permissionsReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { AutoComplete } from 'features/common/AutoCompleteSelect/AutoComplete';
import { IUserSelectListOptions } from 'features/Settings/api/type-def';
import UsersListSelection from 'features/Settings/Components/UsersListSelection/UsersListSelection';
import { UserPayloadInterface } from 'features/Settings/Workflow/api/type-def';

import { createGroupData, getRolesTableData, updateGroupData } from '../api';
import {
	ICreateGroupRequestPayload,
	IRole,
	IRolesOptionsProps,
} from '../api/type-def';

import styles from './CreateGroup.module.scss';

const { CREATELABEL, EDITLABEL } = DATA_ACCESS_PERMISSIONS_ACTIONS;

const { OK, CREATED } = HTTP_STATUS_CODES;

const { searchFieldBackground, searchFieldBorderRadius } = styles;
export const StyledTextField = styled(TextField)({
	background: searchFieldBackground,
	borderRadius: searchFieldBorderRadius,
	width: '100%',
});
const CreateGroup: FunctionComponent = () => {
	const { drawerCloseIconColor } = styles;
	const dispatch = useDispatch();
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const groupPermissionDrawerState = useSelector(
		(state: RootState) => state.permissionsmodule.permissionGroupDrawer,
	);
	const selectedGroupDetails = useSelector(
		(state: RootState) => state.permissionsmodule.groupDetails,
	);

	const emptyGroup = {
		groupName: '',
		groupDescription: '',
	};
	const defaultAddformValues = {
		groupName: selectedGroupDetails?.data?.groupName,
		groupDescription: selectedGroupDetails?.data?.groupDesc,
	};
	const {
		control,
		reset,
		getValues,
		formState: { isDirty, isValid },
	} = useForm<any>({
		mode: 'onChange',
		defaultValues: defaultAddformValues || emptyGroup,
	});
	const [paramsData] = useState({
		sortKey: '',
		pageSize: 500,
		pageNumber: PAGINATION_CONSTANTS.PAGE_NUMBER,
		sortDirection: '',
	});
	const [roleOptions, setRoleOptions] = useState<IRolesOptionsProps[]>([]);
	const [isRolesLoading, setIsRolesLoading] = useState<boolean>(true);
	const [selectedUserMailList, setSelectedUserMailList] = useState<any[]>([]);
	const [selectedRolesList, setSelectedRolesList] = useState<
		IRolesOptionsProps[]
	>([]);
	const [formData, setFormData] = useState({
		groupName: selectedGroupDetails?.data?.groupName || '',
		groupDescription: selectedGroupDetails?.data?.groupDesc || '',
	});
	const [, setAddUsersDisabled] = useState<boolean>(true);
	const [isRoleValid, setIsRoleValid] = useState<boolean>(
		selectedGroupDetails.type === CREATELABEL,
	);
	const [isUserValid, setIsUserValid] = useState<boolean>(
		selectedGroupDetails.type === CREATELABEL,
	);
	const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(
		selectedGroupDetails.type === CREATELABEL,
	);
	const [, setExistingUsersData] = useState<IUserSelectListOptions[]>([]);
	const getRolesList = async () => {
		const response: any = await getRolesTableData(paramsData);
		if (response?.statusCode === OK) {
			const rolesList: IRole[] = response.responseBody.content;
			const options: IRolesOptionsProps[] = rolesList.map((role: IRole) => ({
				label: role.roleName,
				id: role.roleId,
				checked: false,
				displayLabel: role.roleName,
			}));
			if (selectedGroupDetails?.type === EDITLABEL) {
				const rolesSelected: IRole[] = selectedGroupDetails?.data.roles;
				const selected: IRolesOptionsProps[] = rolesSelected?.map(
					(role: IRole) => ({
						label: role.roleName,
						id: role.roleId,
						checked: true,
						displayLabel: role.roleName,
					}),
				);
				setSelectedRolesList(selected);
			}
			setRoleOptions(options);
			setIsRolesLoading(false);
		}
	};
	const handleInputChange = (field: any, value: any) => {
		setFormData((prevFormData: any) => ({
			...prevFormData,
			[field]: value,
		}));
	};
	const onRolesSelect = (roles: any) => {
		setSelectedRolesList(roles);
		const selectedUsers = selectedGroupDetails?.data?.roles || [];
		if (selectedGroupDetails.type === CREATELABEL) {
			setIsRoleValid(true);
		} else if (
			roles?.length === 0 &&
			selectedUsers.length === 0 &&
			selectedGroupDetails.type === EDITLABEL
		) {
			setIsRoleValid(false);
		} else if (
			!_.isEqual(selectedUsers, roles) &&
			selectedGroupDetails.type === EDITLABEL
		) {
			setIsRoleValid(true);
		} else {
			setIsRoleValid(false);
		}
	};
	const onUserSelect = (data: any) => {
		setSelectedUserMailList(data);
		const selectedUsers = selectedGroupDetails?.data?.users || [];
		if (selectedGroupDetails.type === CREATELABEL) {
			setIsUserValid(true);
		} else if (
			data?.length === 0 &&
			selectedUsers.length === 0 &&
			selectedGroupDetails.type === EDITLABEL
		) {
			setIsUserValid(false);
		} else if (
			!_.isEqual(selectedUsers, data) &&
			selectedGroupDetails.type === EDITLABEL
		) {
			setIsUserValid(true);
		} else {
			setIsUserValid(false);
		}
		setAddUsersDisabled(false);
	};
	const buildCreatePayload = () => {
		let rolesId = [];
		let usersId: any[] = [];
		if (selectedRolesList && selectedRolesList.length > 0) {
			rolesId = selectedRolesList.map((ele: any) => ele?.id);
		}
		if (selectedUserMailList && selectedUserMailList.length > 0) {
			const usersList = selectedUserMailList.map((ele: any) => {
				const userObj = {} as UserPayloadInterface;
				userObj.email = ele.email;
				userObj.userName = ele.userName;
				userObj.firstName = ele.firstName;
				userObj.lastName = ele.lastName;
				return userObj;
			});

			usersId = usersList;
		}
		const paramsObj: any = {};
		if (selectedGroupDetails.type === EDITLABEL) {
			const editFormData = getValues();
			paramsObj.groupName = editFormData.groupName.trim();
			paramsObj.groupDesc = editFormData.groupDescription.trim();
		} else {
			paramsObj.groupName = formData.groupName.trim();
			paramsObj.groupDesc = formData.groupDescription.trim();
		}
		paramsObj.roles = rolesId;
		paramsObj.users = usersId;
		paramsObj.loggedInUser = loggedInUser?.userId as string;
		return paramsObj;
	};
	const onCreateGroup = async () => {
		const groupParams: ICreateGroupRequestPayload = buildCreatePayload();
		if (selectedGroupDetails.type === EDITLABEL) {
			const { groupId } = selectedGroupDetails.data;
			const response = await updateGroupData(groupParams, groupId);
			if (response?.statusCode === OK) {
				drawerCloseHandler();
				displaySuccessMessage('Groups Updated Successfully.');
			} else {
				displayErrorMessage(response);
			}
		} else {
			const response = await createGroupData(groupParams);
			if (response?.statusCode === CREATED) {
				drawerCloseHandler();
				displaySuccessMessage('Group Created Successfully.');
			} else {
				displayErrorMessage(response);
			}
		}
	};
	const displaySuccessMessage = (sucessMsg: string) => {
		dispatch(
			setToastMessage({
				message: sucessMsg,
				severity: 'success',
				isToastOpen: true,
			}),
		);
	};
	const displayErrorMessage = (response: any) => {
		const { errorBody }: any = response;
		const errorMsg = errorBody?.errorMessage as string;
		dispatch(
			setToastMessage({
				message: errorMsg,
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};
	const drawerCloseHandler = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
		reset({ ...emptyGroup });
		dispatch(setGroupPermissionDrawer(!groupPermissionDrawerState));
	}, [groupPermissionDrawerState]);
	useEffect(() => {
		if (selectedGroupDetails.type === CREATELABEL && isValid) {
			setIsSaveDisabled(false);
		} else if (selectedGroupDetails.type === EDITLABEL && !isValid) {
			setIsSaveDisabled(true);
		} else if (
			selectedGroupDetails.type === EDITLABEL &&
			(isRoleValid || isUserValid || (isValid && isDirty))
		) {
			setIsSaveDisabled(false);
		} else {
			setIsSaveDisabled(true);
		}
	}, [isDirty, isValid, isRoleValid, isUserValid]);
	useEffect(() => {
		getRolesList();
	}, [paramsData]);
	useEffect(() => {
		if (selectedGroupDetails.type === EDITLABEL) {
			setExistingUsersData(selectedGroupDetails.data.users);
			setSelectedUserMailList(selectedGroupDetails.data.users);
		}
	}, []);
	return (
		<Box
			className={styles['group-detailed-container']}
			role="presentation"
			data-testid="permissions-group-container"
		>
			<div className={styles['grouppermissiontop-div']}>
				<div
					className={styles['grouppermissiontext-background']}
					data-testid="grouppermissiontop-div"
				>
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={2}
						data-testid="close-button-stack"
					>
						<IconButton
							size="medium"
							onClick={drawerCloseHandler}
							data-testid="close-grouppermissionbtn"
						>
							<DrawerClose fill={drawerCloseIconColor} />
						</IconButton>
					</Stack>
					<Box className={styles['grouppermissiontext-box']}>
						<Stack direction="row" justifyContent="space-between">
							<Typography
								className={styles['grouppermissiontext-name']}
								data-testid="edit-text"
							>
								{selectedGroupDetails?.type === 'Create'
									? toSentenceCase('Create Group')
									: toSentenceCase('Edit Group')}
							</Typography>
						</Stack>
					</Box>
				</div>
			</div>
			<Grid
				item
				className={styles['group-create-form-layout']}
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
			>
				{!isRolesLoading ? (
					<form>
						<Grid container>
							<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
								<Controller
									name="groupName"
									control={control}
									rules={{
										required: {
											value: true,
											message: toSentenceCase('Please enter Group name'),
										},
										validate: (value) => !!value.trim(),
									}}
									render={({
										field: { name, value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldWithLabel
											label={toSentenceCase('Group Name')}
											name={name}
											id="group-name"
											value={value}
											inputProps={{
												'data-testid': 'group-name',
											}}
											onChange={(e) => {
												onChange(e);
												handleInputChange(name, e.target.value);
											}}
											error={!!error}
											required
											helperText={error ? error?.message : null}
											className={styles['groupname-field-width']}
											positionClass={styles['word-limit-position']}
											wordLimit={250}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
								<Controller
									name="groupDescription"
									control={control}
									rules={{
										required: {
											value: true,
											message: 'Please enter Group Description',
										},
										validate: (value) => !!value.trim(),
									}}
									render={({
										field: { name, value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldWithLabel
											label={toSentenceCase('Group Description (To-Do)')}
											name={name}
											id="group-desc-name"
											value={value}
											inputProps={{
												'data-testid': 'group-desc-name',
											}}
											onChange={(e) => {
												onChange(e);
												handleInputChange(name, e.target.value);
											}}
											error={!!error}
											required
											helperText={error ? error?.message : null}
											className={styles['groupname-field-width']}
											multiLine
											wordLimit={420}
											positionClass={styles['word-limit-position']}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
								{roleOptions.length > 0 && (
									<div data-testid="roles-list">
										<AutoComplete
											data={roleOptions}
											onUserSelect={onRolesSelect}
											selectedData={selectedRolesList}
											type="email"
											key="email"
											viewChange
											displayLabel="Roles"
										/>
									</div>
								)}
							</Grid>

							<Grid
								data-testid="users-area"
								item
								xs={12}
								sm={12}
								md={12}
								xl={12}
								lg={12}
								p={1}
							>
								<UsersListSelection
									onUserListItemSelect={onUserSelect}
									existingUsersData={selectedUserMailList}
									actionType={
										selectedGroupDetails?.type === EDITLABEL ? 'edit' : 'create'
									}
								/>
							</Grid>
						</Grid>
					</form>
				) : (
					<Loader show={isRolesLoading} />
				)}
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				p={1}
				className={styles['btn-position']}
			>
				<Stack direction="row" className={styles['btn-grouppermissionbutton']}>
					<ButtonComp
						clickHandler={drawerCloseHandler}
						btnType="secondary"
						data-testid="cancel-btn"
						compClass={styles['btn-cancel']}
					>
						Cancel
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						clickHandler={onCreateGroup}
						disabled={isSaveDisabled}
						data-testid="save-btn"
						compClass={styles['btn-save']}
					>
						{selectedGroupDetails?.type === 'Create' ? 'Create' : 'Save'}
					</ButtonComp>
				</Stack>
			</Grid>
		</Box>
	);
};
export default CreateGroup;
